import { useId, type ReactNode } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';

import './InfoTooltip.scss';

interface InfoTooltipProps {
  readonly children: ReactNode;
  /**
   * The field this tooltip explains, folded into the badge's accessible name
   * ("More information about Preserve wall-clock time"). Omitted, every badge on
   * a form announces the same generic name, so a screen-reader user listing the
   * form's elements can't tell which field each one belongs to.
   */
  readonly describes?: string;
}

/**
 * A hoverable info icon carrying explanatory text. The compact counterpart to
 * `Info`'s block callout: use it beside a label when the guidance is worth
 * having but not worth the vertical space it would occupy inline.
 *
 * Not a form control, so it stays interactive inside a `<fieldset disabled>` —
 * the explanation for a greyed-out field remains readable. `tabIndex` makes it
 * keyboard-reachable, which is what surfaces the tooltip for non-pointer users
 * (`OverlayTrigger` triggers on focus as well as hover).
 */
export const InfoTooltip = ({ children, describes }: InfoTooltipProps) => {
  const id = useId();
  return (
    <OverlayTrigger
      placement="right"
      // All three are load-bearing for an icon near the right edge of the window
      // (e.g. the node-submit form, where the row spans the full width):
      //
      // - `flip`: react-bootstrap defaults it to `placement.includes('auto')`,
      //   so an explicit placement DISABLES flipping entirely and the tooltip
      //   just overflows. Opting in is what makes the fallbacks below run.
      // - `container`: rendered inline, the tooltip's containing block ends at
      //   the viewport, so the browser shrink-to-fits it to a few pixels wide
      //   and Popper then measures something that already "fits" — no overflow
      //   to react to. Portalling to `body` keeps its natural width. Bootstrap's
      //   tooltip z-index (1080) is above `.modal` (1055), so this stays correct
      //   inside a dialog.
      // - `fallbackPlacements`: Popper's default fallback for `right` is `left`
      //   alone; the vertical ones cover a viewport too narrow for either side.
      flip
      container={document.body}
      popperConfig={{ modifiers: [{ name: 'flip', options: { fallbackPlacements: ['left', 'top', 'bottom'] } }] }}
      overlay={<Tooltip id={id}>{children}</Tooltip>}
    >
      {/* A literal "i" rather than an icon font: at this size a glyph renders
          fuzzy and sits off the text baseline. aria-label overrides the "i" as
          the accessible name.

          aria-describedby is what makes the text reach a screen reader at all:
          `OverlayTrigger` clones the trigger with a ref and the show/hide
          handlers only, and never associates the overlay with it, so without
          this the tooltip is visible but silent — the badge's name is announced
          and the explanation is not. The referenced id resolves only while the
          tooltip is shown; a dangling IDREF is ignored, which is the same
          arrangement Bootstrap's own tooltip JS produces. */}
      <span
        className="info-tooltip"
        tabIndex={0}
        aria-label={describes === undefined ? 'More information' : `More information about ${describes}`}
        aria-describedby={id}
      >
        i
      </span>
    </OverlayTrigger>
  );
};
